import type { IconGlyph } from "@/icons";
import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes } from "react";
import { Icon, type IconSize } from "./Icon";

/** Classes of `IconBadge`: a glyph centered in a tinted circle or rounded square; `info` renders as `primary`. */
export const iconBadgeVariants = cva("inline-grid shrink-0 place-items-center", {
  variants: {
    size: {
      xs: "h-6 w-6",
      sm: "h-7 w-7",
      md: "h-8 w-8",
      lg: "h-10 w-10",
      xl: "h-16 w-16",
      "2xl": "h-20 w-20",
    },
    tone: {
      primary: "",
      info: "",
      neutral: "",
      success: "",
      warning: "",
      danger: "",
      secondary: "",
      "on-media": "",
    },
    variant: { soft: "", solid: "", surface: "bg-card/70" },
    shape: { circle: "rounded-full", rounded: "rounded-control" },
  },
  compoundVariants: [
    { variant: "soft", tone: ["primary", "info"], class: "bg-primary/10 text-primary" },
    { variant: "soft", tone: "neutral", class: "bg-card-text/10 text-card-text/75" },
    { variant: "soft", tone: "success", class: "bg-success/10 text-success" },
    { variant: "soft", tone: "warning", class: "bg-warning/10 text-warning" },
    { variant: "soft", tone: "danger", class: "bg-danger/10 text-danger" },
    { variant: "soft", tone: "secondary", class: "bg-secondary/10 text-secondary" },
    { variant: "soft", tone: "on-media", class: "bg-on-media/20 text-on-media" },

    { variant: "solid", tone: ["primary", "info"], class: "bg-primary text-primary-foreground" },
    { variant: "solid", tone: "neutral", class: "bg-gray-scale-500 text-on-media" },
    { variant: "solid", tone: "success", class: "bg-success text-on-media" },
    { variant: "solid", tone: "warning", class: "bg-warning text-warning-foreground" },
    { variant: "solid", tone: "danger", class: "bg-danger text-danger-foreground" },
    { variant: "solid", tone: "secondary", class: "bg-secondary text-secondary-foreground" },
    { variant: "solid", tone: "on-media", class: "bg-on-media text-card-text" },

    { variant: "surface", tone: ["primary", "info"], class: "text-primary" },
    { variant: "surface", tone: "neutral", class: "text-card-text/75" },
    { variant: "surface", tone: "success", class: "text-success" },
    { variant: "surface", tone: "warning", class: "text-warning" },
    { variant: "surface", tone: "danger", class: "text-danger" },
    { variant: "surface", tone: "secondary", class: "text-secondary" },
    { variant: "surface", tone: "on-media", class: "text-on-media" },
  ],
  defaultVariants: { size: "md", tone: "primary", variant: "soft", shape: "circle" },
});

type IconBadgeSize = NonNullable<VariantProps<typeof iconBadgeVariants>["size"]>;

/** Glyph size that keeps the icon at half the container. */
export const ICON_BADGE_GLYPH_SIZE: Record<IconBadgeSize, IconSize> = {
  xs: "xs",
  sm: "sm",
  md: "sm",
  lg: "md",
  xl: "2xl",
  "2xl": "3xl",
};

/** Props of `IconBadge`; decorative unless `label` is set. */
export type IconBadgeProps = VariantProps<typeof iconBadgeVariants> &
  Omit<HTMLAttributes<HTMLSpanElement>, "children"> & {
    icon: IconGlyph;
    weight?: "regular" | "bold" | "fill" | "duotone";
    label?: string;
    /** Overrides the half-container glyph (a section header icon that must read at 20px). */
    glyphSize?: IconSize;
  };

/** Icon inside a tinted container (section headers, empty states, timeline steps). */
export const IconBadge = forwardRef<HTMLSpanElement, IconBadgeProps>(function IconBadge(
  { icon, size, tone, variant, shape, weight, label, glyphSize, className, ...rest },
  ref,
) {
  return (
    <span
      {...rest}
      ref={ref}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn(iconBadgeVariants({ size, tone, variant, shape }), className)}
    >
      <Icon as={icon} size={glyphSize ?? ICON_BADGE_GLYPH_SIZE[size ?? "md"]} weight={weight} />
    </span>
  );
});
